import { useMemo, useState } from 'react';
import {
  type OutOfOfficeRecord,
  useCancelOutOfOffice,
  useCreateOutOfOffice,
  useMyOutOfOfficeQuery,
  useUpdateOutOfOffice,
} from '@access-cms/api-client';
import { Alert } from '@cmsgov/design-system';

import { Button, ButtonVariation } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { RequiredFieldsLegend } from '../../components/RequiredFieldsLegend';
import Tooltip from '../../components/Tooltip';
import { useAlerts } from '../../hooks';
import { ASSET_PATHS } from '../../utils/assetPath';
import ConfirmActionModal from '../contract/components/ConfirmActionModal/ConfirmActionModal';

interface PeriodDates {
  endDate: Date | null;
  startDate: Date | null;
}

interface PeriodRowProps {
  periods: OutOfOfficeRecord[];
  period: OutOfOfficeRecord;
}

const OOO_OVERLAP_ERROR_CODE = '400.EM.USER.1102';
const OOO_OVERLAP_ERROR_MESSAGE =
  'One or more selected dates are already included in another Out of Office schedule. Choose dates that do not overlap an existing schedule.';

function getOutOfOfficeErrorMessage(error: unknown, fallbackMessage: string): string {
  const structuredError =
    typeof error === 'object' && error !== null
      ? (error as {
          code?: unknown;
          message?: unknown;
          statusCode?: unknown;
        })
      : undefined;
  const errorCode = typeof structuredError?.code === 'string' ? structuredError.code : undefined;
  const errorMessage = typeof structuredError?.message === 'string' ? structuredError.message : undefined;
  const statusCode = typeof structuredError?.statusCode === 'number' ? structuredError.statusCode : undefined;

  if (
    errorCode === OOO_OVERLAP_ERROR_CODE ||
    errorMessage?.toLowerCase().includes('overlap') ||
    (statusCode === 400 && errorMessage === 'An unexpected error occurred')
  ) {
    return OOO_OVERLAP_ERROR_MESSAGE;
  }

  return errorMessage || fallbackMessage;
}

function isoToDate(value?: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return Number.isNaN(date.getTime()) ? null : date;
}

function dateToIso(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function todayAtMidnight(): Date {
  const easternDate = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/New_York',
    year: 'numeric',
  }).format(new Date());

  return isoToDate(easternDate) ?? new Date();
}

function validateDates({ endDate, startDate }: PeriodDates): string {
  if (!startDate || !endDate) return 'Enter both a Start Date and an End Date.';
  if (endDate < startDate) return 'End Date must be on or after Start Date.';
  if (endDate < todayAtMidnight()) return 'End Date cannot be before today.';

  return '';
}

function overlapsExistingPeriod(
  { endDate, startDate }: PeriodDates,
  periods: OutOfOfficeRecord[],
  excludedPeriodId?: string,
): boolean {
  if (!startDate || !endDate) return false;

  const startDateIso = dateToIso(startDate);
  const endDateIso = dateToIso(endDate);

  return periods.some(
    (period) =>
      period.id !== excludedPeriodId &&
      ['ACTIVE', 'SCHEDULED'].includes(period.status ?? '') &&
      period.startDate <= endDateIso &&
      period.endDate >= startDateIso,
  );
}

function compareCurrentPeriods(first: OutOfOfficeRecord, second: OutOfOfficeRecord): number {
  if (first.status !== second.status) {
    if (first.status === 'ACTIVE') return -1;
    if (second.status === 'ACTIVE') return 1;
  }

  const startDateComparison = first.startDate.localeCompare(second.startDate);
  if (startDateComparison !== 0) {
    return startDateComparison;
  }

  return first.endDate.localeCompare(second.endDate);
}

function PeriodRow({ period, periods }: PeriodRowProps) {
  const { successAlert } = useAlerts();
  const persistedStartDate = isoToDate(period.startDate);
  const persistedEndDate = isoToDate(period.endDate);
  const [dates, setDates] = useState<PeriodDates>({
    endDate: persistedEndDate,
    startDate: persistedStartDate,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const updatePeriod = useUpdateOutOfOffice();
  const cancelPeriod = useCancelOutOfOffice();
  const isActive = period.status === 'ACTIVE';
  const isDirty =
    (dates.startDate ? dateToIso(dates.startDate) : '') !== period.startDate ||
    (dates.endDate ? dateToIso(dates.endDate) : '') !== period.endDate;
  const overlapErrorMessage = errorMessage === OOO_OVERLAP_ERROR_MESSAGE ? errorMessage : undefined;

  const save = async () => {
    const validationError = validateDates(dates);
    if (validationError || !dates.startDate || !dates.endDate) {
      setErrorMessage(validationError);

      return;
    }

    if (overlapsExistingPeriod(dates, periods, period.id)) {
      setErrorMessage(OOO_OVERLAP_ERROR_MESSAGE);

      return;
    }

    setErrorMessage('');
    try {
      await updatePeriod.mutateAsync({
        body: {
          end_date: dateToIso(dates.endDate),
          ...(isActive ? {} : { start_date: dateToIso(dates.startDate) }),
        },
        pathParams: { id: period.id },
      });
      successAlert({
        dismissInSeconds: 30,
        title: 'Out of Office period updated successfully',
      });
    } catch (error) {
      setErrorMessage(getOutOfOfficeErrorMessage(error, 'Unable to update this Out of Office period.'));
    }
  };

  const removeOrEnd = async () => {
    setErrorMessage('');
    try {
      await cancelPeriod.mutateAsync({ id: period.id });
      successAlert({
        dismissInSeconds: 30,
        title: isActive ? 'Out of Office ended successfully' : 'Out of Office schedule removed successfully',
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to update this Out of Office period.');
      throw error;
    }
  };

  const actionLabel = isActive ? 'End Out of Office Now' : 'Remove Schedule';

  return (
    <>
      <fieldset
        className='profile-ooo__period'
        data-testid={`profile-ooo-period-${period.id}`}>
        <legend className={`profile-ooo__status profile-ooo__status--${isActive ? 'active' : 'scheduled'}`}>
          {isActive ? 'Active' : 'Scheduled'}
        </legend>
        <div className='profile-ooo__fields'>
          <DatePicker
            required
            data-testid={`profile-ooo-start-${period.id}`}
            disabled={isActive}
            fromDate={isActive ? undefined : todayAtMidnight()}
            label='Start Date'
            name={`ooo-start-${period.id}`}
            value={dates.startDate ?? ''}
            onDateChange={(startDate) => {
              setDates((current) => ({ ...current, startDate }));
              setErrorMessage('');
            }}
          />
          <DatePicker
            required
            data-testid={`profile-ooo-end-${period.id}`}
            errorMessage={overlapErrorMessage}
            fromDate={todayAtMidnight()}
            label='End Date'
            name={`ooo-end-${period.id}`}
            value={dates.endDate ?? ''}
            onDateChange={(endDate) => {
              setDates((current) => ({ ...current, endDate }));
              setErrorMessage('');
            }}
          />
          <div className='profile-ooo__row-actions'>
            <Button
              isAutoWidth
              data-testid={`profile-ooo-save-${period.id}`}
              disabled={!isDirty || updatePeriod.isPending}
              type='button'
              variation={ButtonVariation.SOLID}
              onClick={save}>
              Save Changes
            </Button>
            <Button
              isAutoWidth
              data-testid={`profile-ooo-remove-${period.id}`}
              type='button'
              variation={ButtonVariation.SOLID}
              onClick={() => setIsConfirmOpen(true)}>
              {actionLabel}
            </Button>
          </div>
        </div>
        {errorMessage && !overlapErrorMessage && (
          <p
            className='profile-ooo__error'
            role='alert'>
            {errorMessage}
          </p>
        )}
      </fieldset>
      <ConfirmActionModal
        confirmLabel={isActive ? 'End Out of Office Now' : 'Remove Schedule'}
        data-testid={`profile-ooo-confirm-${period.id}`}
        description={
          isActive
            ? 'End your Out of Office period now? New work will stop routing to your Delegate Federal Approver. Reviews already assigned to the Delegate Federal Approver will remain with them.'
            : 'Remove this scheduled Out of Office period? It will no longer take effect.'
        }
        isConfirming={cancelPeriod.isPending}
        isOpen={isConfirmOpen}
        title={actionLabel}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={removeOrEnd}
      />
    </>
  );
}

function OutOfOfficeSection() {
  const { successAlert } = useAlerts();
  const outOfOfficeQuery = useMyOutOfOfficeQuery();
  const createPeriod = useCreateOutOfOffice();
  const [draft, setDraft] = useState<PeriodDates | null>(null);
  const [draftError, setDraftError] = useState('');

  const currentPeriods = useMemo(
    () =>
      (outOfOfficeQuery.data?.data ?? [])
        .filter((period) => ['ACTIVE', 'SCHEDULED'].includes(period.status ?? ''))
        .sort(compareCurrentPeriods),
    [outOfOfficeQuery.data?.data],
  );
  const hasScheduledPeriod = currentPeriods.some((period) => period.status === 'SCHEDULED');
  const draftOverlapErrorMessage = draftError === OOO_OVERLAP_ERROR_MESSAGE ? draftError : undefined;

  const saveDraft = async () => {
    if (!draft) return;
    const validationError = validateDates(draft);
    if (validationError || !draft.startDate || !draft.endDate) {
      setDraftError(validationError);

      return;
    }

    if (overlapsExistingPeriod(draft, currentPeriods)) {
      setDraftError(OOO_OVERLAP_ERROR_MESSAGE);

      return;
    }

    setDraftError('');
    try {
      await createPeriod.mutateAsync({
        end_date: dateToIso(draft.endDate),
        start_date: dateToIso(draft.startDate),
      });
      setDraft(null);
      successAlert({
        dismissInSeconds: 30,
        title: 'Out of Office period scheduled successfully',
      });
    } catch (error) {
      setDraftError(getOutOfOfficeErrorMessage(error, 'Unable to save this Out of Office period.'));
    }
  };

  return (
    <div
      className='profile-ooo'
      data-testid='profile-ooo-section'>
      <div className='profile-page__divider' />
      <div className='profile-ooo__heading-row'>
        <div className='profile-page__password-details'>
          <div className='profile-page__label'>Out of Office</div>
          <p className='profile-page__value profile-ooo__intro'>
            {currentPeriods.length === 0 && !draft
              ? 'There are currently no Out of Office dates set.'
              : 'Please set your Delegate Federal Approver in Contract Settings for each contract you manage.'}
          </p>
          {(draft || hasScheduledPeriod) && (
            <p className='profile-page__value profile-ooo__timezone'>Dates are inclusive and based on Eastern Time.</p>
          )}
          {(draft || currentPeriods.length > 0) && <RequiredFieldsLegend data-testid='profile-ooo-required-fields' />}
        </div>
        <div className='profile-page__password-actions'>
          <Tooltip
            data-testid='profile-ooo-create-tooltip'
            placement='top'
            title='Create a new period you will be Out of Office. You will need to set a start and end date for each new Out of Office period.'>
            <img
              alt=''
              aria-hidden='true'
              className='profile-page__change-password-icon'
              src={ASSET_PATHS.INFO_CIRCLE_ICON}
            />
          </Tooltip>
          <button
            className='profile-page__change-password profile-ooo__create'
            data-testid='profile-ooo-create'
            disabled={draft !== null}
            type='button'
            onClick={() => setDraft({ endDate: null, startDate: null })}>
            Create New
          </button>
        </div>
      </div>

      {outOfOfficeQuery.isLoading && <p>Loading Out of Office periods...</p>}
      {outOfOfficeQuery.isError && (
        <Alert
          heading='Unable to load Out of Office periods'
          variation='error'>
          Please try again later.
        </Alert>
      )}

      {draft && (
        <fieldset
          className='profile-ooo__period profile-ooo__period--draft'
          data-testid='profile-ooo-draft'>
          <legend className='profile-ooo__status'>New Out of Office Period</legend>
          <div className='profile-ooo__fields'>
            <DatePicker
              required
              data-testid='profile-ooo-draft-start'
              fromDate={todayAtMidnight()}
              label='Start Date'
              name='ooo-draft-start'
              value={draft.startDate ?? ''}
              onDateChange={(startDate) => {
                setDraft((current) => (current ? { ...current, startDate } : current));
                setDraftError('');
              }}
            />
            <DatePicker
              required
              data-testid='profile-ooo-draft-end'
              errorMessage={draftOverlapErrorMessage}
              fromDate={draft.startDate ?? todayAtMidnight()}
              label='End Date'
              name='ooo-draft-end'
              value={draft.endDate ?? ''}
              onDateChange={(endDate) => {
                setDraft((current) => (current ? { ...current, endDate } : current));
                setDraftError('');
              }}
            />
            <div className='profile-ooo__row-actions'>
              <Button
                isAutoWidth
                data-testid='profile-ooo-draft-save'
                disabled={!draft.startDate || !draft.endDate}
                isLoading={createPeriod.isPending}
                type='button'
                variation={ButtonVariation.SOLID}
                onClick={saveDraft}>
                Save Period
              </Button>
              <Button
                isAutoWidth
                data-testid='profile-ooo-draft-cancel'
                type='button'
                variation={ButtonVariation.GHOST}
                onClick={() => {
                  setDraft(null);
                  setDraftError('');
                }}>
                Cancel
              </Button>
            </div>
          </div>
          {draftError && !draftOverlapErrorMessage && (
            <p
              className='profile-ooo__error'
              role='alert'>
              {draftError}
            </p>
          )}
        </fieldset>
      )}

      {currentPeriods.map((period) => (
        <PeriodRow
          key={`${period.id}-${period.startDate}-${period.endDate}-${period.status}`}
          period={period}
          periods={currentPeriods}
        />
      ))}
    </div>
  );
}

export default OutOfOfficeSection;
